/**
 * Menu root component with viewport awareness.
 *
 * Wraps Bloom's Root with automatic viewport detection. When a submenu
 * would overflow the viewport, the entire menu shifts up.
 */

import type { CSSProperties, ReactNode, RefCallback } from "react";
import * as BloomMenu from "bloom-menu";
import { useCallback, useEffect, useRef, useState } from "react";

import { ViewportAwareContext } from "./menu-context";

export interface MenuRootProps {
  children: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchor?: "start" | "end";
  direction?: "top" | "bottom";
  style?: CSSProperties;
  className?: string;
}

export function MenuRoot({
  children,
  open,
  onOpenChange,
  anchor,
  direction,
  style,
  className,
}: MenuRootProps) {
  const [offset, setOffset] = useState(0);
  const subMenuRef = useRef<HTMLElement | null>(null);
  const hasMeasuredRef = useRef(false);

  const registerSubMenu: RefCallback<HTMLElement> = useCallback((element) => {
    subMenuRef.current = element;

    if (!element) {
      hasMeasuredRef.current = false;
      return;
    }

    if (hasMeasuredRef.current) {
      return;
    }

    // Wait for Bloom's animation to position the submenu
    setTimeout(() => {
      if (!subMenuRef.current || hasMeasuredRef.current) {
        return;
      }
      hasMeasuredRef.current = true;

      const rect = subMenuRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const overflow = rect.bottom - viewportHeight;

      if (overflow > 0) {
        setOffset(-(overflow + 16));
      }
    }, 100);
  }, []);

  // Reset when menu closes
  useEffect(() => {
    if (!open) {
      setOffset(0);
      hasMeasuredRef.current = false;
    }
  }, [open]);

  return (
    <ViewportAwareContext.Provider value={{ registerSubMenu, offset }}>
      <div
        className={className}
        style={{
          ...style,
          transform: offset !== 0 ? `translateY(${offset}px)` : undefined,
          transition: "transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
      >
        <BloomMenu.Root
          onOpenChange={onOpenChange}
          open={open}
          {...(anchor !== undefined && { anchor })}
          {...(direction !== undefined && { direction })}
        >
          {children}
        </BloomMenu.Root>
      </div>
    </ViewportAwareContext.Provider>
  );
}
